"use client";

import { ShieldCheck } from "@repo/ui/components/icons";
import { useCallback, useEffect, useRef, useState } from "react";

import { getVerifications, submitIdentity, submitShahkar, type Verifications, type VerificationStep } from "../../../lib/api-client";
import { DigitInput, jalaliDate } from "../../components/DigitInput";
import { fieldClass, PanelHeader, panelClass, primaryButtonClass } from "./panel";
import { Loading, SkeletonBlock } from "@repo/ui/components/skeleton";

// Identity verification steps (F114): step 1 Shahkar (national ID + the
// account's mobile) makes the user verified; step 2 (Jalali birth date) only
// when staff require it. This component shows whichever required step is
// next; the timeline page (/app/profile/verifications) shows all of them.

const ERRORS: Record<string, string> = {
  shahkar_mismatch: "این شماره موبایل به نام صاحب این کد ملی ثبت نشده است. کد ملی را بررسی کنید.",
  identity_mismatch: "تاریخ تولد با سوابق ثبت احوال مطابقت ندارد.",
  shahkar_unavailable: "اختلال در سرویس دهنده؛ کمی بعد دوباره امتحان کنید.",
  identity_unavailable: "اختلال در سرویس دهنده؛ کمی بعد دوباره امتحان کنید.",
  national_id_mismatch: "کد ملی با کد ملی سطح یک یکی نیست.",
  national_id_already_linked: "این کد ملی قبلا برای حساب دیگری ثبت شده است.",
  rate_limited: "تعداد تلاش‌های امروز بیش از حد مجاز است؛ فردا دوباره تلاش کنید.",
  invalid_request: "اطلاعات وارد‌شده درست نیست.",
};
const errorText = (code: string | null | undefined) => (code && ERRORS[code]) || "بررسی انجام نشد؛ دوباره تلاش کنید.";

/** Step 1: the national ID of the owner of the account's mobile number. */
export function ShahkarStep({ onPassed, large = false }: { onPassed: () => void; large?: boolean }) {
  const [nationalId, setNationalId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit() {
    setBusy(true);
    setError(null);
    const { data } = await submitShahkar(nationalId).catch(() => ({ data: null }));
    setBusy(false);
    if (data && "ok" in data) onPassed();
    else setError(errorText(data && "error" in data ? data.error : null));
  }
  return (
    <div className="flex flex-col gap-3">
      <DigitInput value={nationalId} onChange={(v) => (setNationalId(v), setError(null))} maxLength={10} placeholder="کد ملی" className={fieldClass} aria-label="کد ملی" />
      <button
        type="button"
        disabled={busy || nationalId.length !== 10}
        onClick={() => void submit()}
        className={large ? `${primaryButtonClass} h-12 w-full text-base` : `${primaryButtonClass} self-start`}
      >
        {busy ? "در حال استعلام…" : "استعلام و تایید"}
      </button>
      {error ? <p className="text-ink-red text-sm">{error}</p> : null}
    </div>
  );
}

/** Step 2 (سطح دو): the national ID (must match step 1's) and the Jalali birth date. */
export function IdentityStep({ onPassed, large = false }: { onPassed: () => void; large?: boolean }) {
  const [nationalId, setNationalId] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit() {
    setBusy(true);
    setError(null);
    const { data } = await submitIdentity(birthDate, nationalId).catch(() => ({ data: null }));
    setBusy(false);
    if (data && "ok" in data) onPassed();
    else setError(errorText(data && "error" in data ? data.error : null));
  }
  return (
    <div className="flex flex-col gap-3">
      <DigitInput value={nationalId} onChange={(v) => (setNationalId(v), setError(null))} maxLength={10} placeholder="کد ملی" className={fieldClass} aria-label="کد ملی" />
      <DigitInput
        value={birthDate}
        onChange={(v) => (setBirthDate(v), setError(null))}
        maxLength={8}
        format={jalaliDate}
        placeholder="تاریخ تولد (۱۳۶۹/۰۱/۲۵)"
        className={fieldClass}
        aria-label="تاریخ تولد شمسی"
      />
      <button
        type="button"
        disabled={busy || nationalId.length !== 10 || birthDate.length !== 8}
        onClick={() => void submit()}
        className={large ? `${primaryButtonClass} h-12 w-full text-base` : `${primaryButtonClass} self-start`}
      >
        {busy ? "در حال استعلام…" : "استعلام و تایید"}
      </button>
      {error ? <p className="text-ink-red text-sm">{error}</p> : null}
    </div>
  );
}

/** The next required step that isn't done, or null when the user is verified. */
export function nextStep(v: Verifications): VerificationStep | null {
  return v.steps.find((s) => s.required && s.status !== "passed") ?? null;
}

// `embedded`: inside the checkout / sign-in steps, which bring their own frame.
export function VerificationForm({ onVerified, embedded = false }: { onVerified: () => void; embedded?: boolean }) {
  const [v, setV] = useState<Verifications | null>(null);
  // Callers pass an inline function; keep the latest without re-running the load.
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;
  const load = useCallback(async () => {
    const { data } = await getVerifications();
    if (!data) return;
    setV(data);
    if (data.verificationStatus === "verified") onVerifiedRef.current();
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const step = v ? nextStep(v) : null;
  const body = !v ? (
    <Loading>
      <SkeletonBlock className="h-12 rounded-xl" />
      <SkeletonBlock className="h-12 rounded-xl" />
    </Loading>
  ) : !step ? null : (
    <div className="flex flex-col gap-3">
      {step.kind === "identity" && step.requiredReason ? <p className="bg-tint-yellow text-ink-yellow rounded-xl p-3 text-xs leading-5">{step.requiredReason}</p> : null}
      {step.kind === "shahkar" ? (
        <ShahkarStep large={embedded} onPassed={() => void load()} />
      ) : (
        <IdentityStep large={embedded} onPassed={() => void load()} />
      )}
    </div>
  );

  if (embedded) return body;
  return (
    <section className={panelClass}>
      <PanelHeader icon={ShieldCheck} title={step?.titleFa ?? "احراز هویت"} description={step?.descriptionFa} />
      {body}
    </section>
  );
}
